import React, { useMemo, useState } from "react";

import { useToasts } from "react-toast-notifications";

import { getQueryParams } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";

import {
  getNorthbeamUtmPlatformParams,
  type NorthbeamUtmPlatform,
  type UtmPlatformParam,
} from "../constants";

interface CheckURLValidityComponentProps {
  readonly platform: NorthbeamUtmPlatform;
  readonly includeSeparator?: boolean;
}

export function CheckUrlValidity({
  platform,
  includeSeparator = true,
}: CheckURLValidityComponentProps) {
  const { addToast } = useToasts();
  const [inputtedUrl, setInputtedUrl] = useState<string>("");

  const platformParams = getNorthbeamUtmPlatformParams({
    platform,
  });

  const validateInputtedUrl = useMemo(
    () => () => {
      let urlParams: URLSearchParams;
      try {
        urlParams = getQueryParams(inputtedUrl);
      } catch (e) {
        if (e instanceof Error && e.name === "InvalidUrlError") {
          return addToast(
            <p>
              URL is malformed and cannot be parsed. Did you forget to add{" "}
              <code>https://</code>?
            </p>,
            {
              appearance: "warning",
            },
          );
        } else {
          throw e;
        }
      }
      const duplicatedOrMissingParams: string[] = platformParams.reduce(
        (prev: string[], curr: UtmPlatformParam) => {
          const values = urlParams.getAll(curr.key);
          if (
            values.length !== 1 ||
            values[0] !== decodeURIComponent(curr.value)
          ) {
            return [...prev, curr.key];
          }
          return prev;
        },
        [],
      );
      if (duplicatedOrMissingParams.length > 0) {
        addToast(
          <p>
            The following param keys were not correctly added{" "}
            {duplicatedOrMissingParams.join(", ")}.
          </p>,
          {
            appearance: "warning",
          },
        );
      } else {
        addToast(<p>The URL appears to be correct.</p>, {
          appearance: "success",
        });
      }
    },
    [inputtedUrl, platformParams, addToast],
  );

  return (
    <div className="flex flex-col">
      {includeSeparator && <hr className="mt-0 mb-3" />}
      <div className="flex flex-col gap-2">
        <div className="control-label" data-testid="url-label">
          <span>
            <i className="fa-regular fa-sharp fa-user" /> &nbsp;
          </span>{" "}
          Check your URL by pasting the full URL here with all your existing
          query params. Please include <code>https://</code>.
        </div>
        <input
          type="text"
          className="form-control"
          data-testid="url-input"
          value={inputtedUrl}
          onChange={(event) => setInputtedUrl(event.target.value)}
        />
        <PrimaryButton
          className="mb-0 mt-1"
          style={{ maxWidth: "fit-content" }}
          onClick={validateInputtedUrl}
        >
          Check Validity
        </PrimaryButton>
      </div>
    </div>
  );
}
